import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { LANGUAGES } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * LanguageSwitcher
 *
 * Compact Globe + 2-letter language code badge with a dropdown listing every
 * supported language (flag emoji + native name). Saves to localStorage via
 * i18next automatically, and — when the user is signed in — also persists
 * the choice to the user profile so it follows them across devices.
 *
 * Variants:
 *   `topbar`  - compact dark-mode-aware pill for the authenticated app
 *   `auth`    - subtle button for login/landing pages
 *   `inline`  - full-width list for the Preferences page
 */
export default function LanguageSwitcher({ variant = 'topbar', testId = 'language-switcher' }) {
  const { i18n } = useTranslation();
  const current = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const setLanguage = async (code) => {
    if (code === i18n.language) return;
    await i18n.changeLanguage(code);
    try { localStorage.setItem('savenes_language', code); } catch { /* noop */ }
    // Best-effort: persist to the user profile so other devices pick it up.
    const token = localStorage.getItem('savenes_token');
    if (token) {
      try {
        await axios.put(
          `${API_URL}/api/auth/profile/language`,
          { language: code },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch { /* offline or unauth — localStorage is the source of truth */ }
    }
  };

  if (variant === 'inline') {
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid={testId}>
        {LANGUAGES.map(l => {
          const active = l.code === current.code;
          return (
            <button
              key={l.code}
              onClick={() => setLanguage(l.code)}
              className={`flex items-center justify-between px-3 py-2.5 rounded-lg border text-left text-sm transition-colors ${
                active
                  ? 'border-[#0E7490] bg-[#ECFEFF] text-[#0A2342]'
                  : 'border-[#E2E8F0] hover:bg-[#F8FAFC] text-[#0A2342]'
              }`}
              data-testid={`lang-option-${l.code}`}
            >
              <span className="flex items-center gap-2">
                <span className="text-lg leading-none">{l.flag}</span>
                <span>
                  <div className="font-medium">{l.native}</div>
                  <div className="text-[10px] text-[#64748B]">{l.label}</div>
                </span>
              </span>
              {active && <Check className="w-4 h-4 text-[#0E7490]" />}
            </button>
          );
        })}
      </div>
    );
  }

  const triggerClass =
    variant === 'auth'
      ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/30 bg-white/10 text-white text-xs hover:bg-white/20 transition-colors'
      : 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-[#E2E8F0] bg-white text-xs text-[#0A2342] hover:bg-[#F8FAFC] transition-colors';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={triggerClass} data-testid={testId} aria-label="Change language">
          <Globe className="w-3.5 h-3.5" />
          <span className="font-semibold uppercase tracking-wide">{current.code}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-[#64748B]">
          Language
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LANGUAGES.map(l => {
          const active = l.code === current.code;
          return (
            <DropdownMenuItem
              key={l.code}
              onSelect={() => setLanguage(l.code)}
              className="cursor-pointer flex items-center justify-between"
              data-testid={`lang-menu-${l.code}`}
            >
              <span className="flex items-center gap-2">
                <span className="text-base leading-none">{l.flag}</span>
                <span className="text-sm">{l.native}</span>
              </span>
              {active && <Check className="w-3.5 h-3.5 text-[#0E7490]" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
